import type { InstrumentRight } from "@skyware-group/iris-api-model";

import { BaseClient } from "../../clients/base/BaseClient";
import type { Paged, PaginationParams } from "../_shared/pagination.types";
import { RIGHTS_ENDPOINTS } from "./rights.consts";
import type { InstrumentRightCreateRequest, InstrumentRightUpdateRequest } from "./rights.types";

/**
 * Instrument Rights API Client (admin only)
 *
 * Handles CRUD for per-instrument access rights. Identified by numeric `id`.
 */
export class RightsClient extends BaseClient {
  /** Fetch a page of access rights for an instrument. */
  async fetchRights(
    instrumentId: number,
    params?: PaginationParams,
    signal?: AbortSignal | null,
  ): Promise<Paged<InstrumentRight>> {
    return this.get<Paged<InstrumentRight>>(RIGHTS_ENDPOINTS.RIGHTS(instrumentId), {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  async fetchRightById(instrumentId: number, id: number, signal?: AbortSignal | null): Promise<InstrumentRight> {
    return this.get<InstrumentRight>(RIGHTS_ENDPOINTS.RIGHT_BY_ID(instrumentId, id), { signal });
  }

  async createRight(
    instrumentId: number,
    data: InstrumentRightCreateRequest,
    signal?: AbortSignal | null,
  ): Promise<InstrumentRight> {
    return this.post<InstrumentRight>(RIGHTS_ENDPOINTS.RIGHTS(instrumentId), { body: data, signal });
  }

  async updateRight(
    instrumentId: number,
    id: number,
    data: InstrumentRightUpdateRequest,
    signal?: AbortSignal | null,
  ): Promise<InstrumentRight> {
    return this.put<InstrumentRight>(RIGHTS_ENDPOINTS.UPDATE_RIGHT(instrumentId, id), {
      body: data,
      signal,
    });
  }

  async deleteRight(instrumentId: number, id: number, signal?: AbortSignal | null): Promise<void> {
    return this.delete<never>(RIGHTS_ENDPOINTS.DELETE_RIGHT(instrumentId, id), { signal });
  }
}
